import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import { Switch } from '@/ui/rapid-ui'
import { SettingsSubheading } from '@/ui/views/settings/shared/settings-typography'
import {
	type OrganisationCandidateProfileSettingsData,
	organisationCandidateProfileSettingsShowQueryKey,
	organisationRelationShowQueryKey,
	useOrganisationCandidateProfileSettingsShow,
	useOrganisationCandidateProfileSettingsUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type CandidateProfileSetting = keyof OrganisationCandidateProfileSettingsData

type CandidateAbility = {
	key: string
	label: string
	setting: CandidateProfileSetting | null
}

export function CandidateProfileSection() {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()
	const queryClient = useQueryClient()

	const { data: settings, isPending, isError } = useOrganisationCandidateProfileSettingsShow(jobillaOrgId, {
		enabled: !!jobillaOrgId,
	})
	const { mutateAsync: updateSettings, isPending: isSaving } = useOrganisationCandidateProfileSettingsUpdate()

	const [saveFailed, setSaveFailed] = useState(false)
	const [justSavedSetting, setJustSavedSetting] = useState<CandidateProfileSetting | null>(null)
	const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const toggle = async (setting: CandidateProfileSetting) => {
		if (!jobillaOrgId || !settings || isSaving) { return }
		setSaveFailed(false)
		try {
			await updateSettings({
				organisation: jobillaOrgId,
				data: { ...settings, [setting]: !settings[setting] },
			})
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: organisationCandidateProfileSettingsShowQueryKey(jobillaOrgId),
				}),
				queryClient.invalidateQueries({ queryKey: organisationRelationShowQueryKey(jobillaOrgId) }),
			])
			setJustSavedSetting(setting)
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
			savedTimerRef.current = setTimeout(() => setJustSavedSetting(null), 2000)
		} catch (err) {
			logger.error(err, { tags: { component: 'CandidateProfileSettings', action: 'update' } })
			setSaveFailed(true)
		}
	}

	const resultsPageAbilities: CandidateAbility[] = [
		{
			key: 'additionalQuestions',
			label: t('settings.candidateCenter.candidateProfile.resultsPage.abilities.additionalQuestions'),
			setting: null,
		},
		{
			key: 'background',
			label: t('settings.candidateCenter.candidateProfile.resultsPage.abilities.background'),
			setting: 'candidateProfileEnabled',
		},
		{
			key: 'cv',
			label: t('settings.candidateCenter.candidateProfile.resultsPage.abilities.cv'),
			setting: 'candidateProfileCvCollectionEnabled',
		},
	]

	const candidateCenterAbilities: CandidateAbility[] = [
		{
			key: 'applications',
			label: t('settings.candidateCenter.candidateProfile.candidateCenter.abilities.applications'),
			setting: null,
		},
		{
			key: 'background',
			label: t('settings.candidateCenter.candidateProfile.candidateCenter.abilities.background'),
			setting: 'candidateProfileEnabled',
		},
		{
			key: 'cv',
			label: t('settings.candidateCenter.candidateProfile.candidateCenter.abilities.cv'),
			setting: 'candidateProfileCvCollectionEnabled',
		},
	]

	return (
		<section className="max-w-[680px]">
			{isError
				? (
					<p className="text-sm text-red-600">
						{t('settings.candidateCenter.candidateProfile.loadError')}
					</p>
				)
				: (
					<div className="space-y-5">
						<SettingSwitch
							id="candidate-profile-enabled"
							label={t('settings.candidateCenter.candidateProfile.enabledLabel')}
							description={t('settings.candidateCenter.candidateProfile.enabledDescription')}
							checked={settings?.candidateProfileEnabled ?? false}
							isLoading={isPending}
							disabled={isSaving}
							justSaved={justSavedSetting === 'candidateProfileEnabled'}
							onToggle={() => void toggle('candidateProfileEnabled')}
						/>
						<SettingSwitch
							id="candidate-profile-cv-collection-enabled"
							label={t('settings.candidateCenter.candidateProfile.cvLabel')}
							description={t('settings.candidateCenter.candidateProfile.cvDescription')}
							checked={settings?.candidateProfileCvCollectionEnabled ?? false}
							isLoading={isPending}
							disabled={isSaving}
							justSaved={justSavedSetting === 'candidateProfileCvCollectionEnabled'}
							onToggle={() => void toggle('candidateProfileCvCollectionEnabled')}
						/>
					</div>
				)}

			{saveFailed
				? (
					<p className="mt-4 text-sm text-red-600">
						{t('settings.candidateCenter.candidateProfile.saveError')}
					</p>
				)
				: null}

			<div className="mt-10 space-y-10">
				<CandidateSurface
					title={t('settings.candidateCenter.candidateProfile.resultsPage.title')}
					description={t('settings.candidateCenter.candidateProfile.resultsPage.description')}
					abilitiesTitle={t('settings.candidateCenter.candidateProfile.resultsPage.abilitiesTitle')}
					abilities={resultsPageAbilities}
					settings={settings}
				/>
				<CandidateSurface
					title={t('settings.candidateCenter.candidateProfile.candidateCenter.title')}
					description={t('settings.candidateCenter.candidateProfile.candidateCenter.description')}
					abilitiesTitle={t('settings.candidateCenter.candidateProfile.candidateCenter.abilitiesTitle')}
					abilities={candidateCenterAbilities}
					settings={settings}
				/>
			</div>
		</section>
	)
}

type CandidateSurfaceProps = {
	title: string
	description: string
	abilitiesTitle: string
	abilities: CandidateAbility[]
	settings: OrganisationCandidateProfileSettingsData | undefined
}

function CandidateSurface({ title, description, abilitiesTitle, abilities, settings }: CandidateSurfaceProps) {
	const { t } = useTranslation('common')

	return (
		<section>
			<SettingsSubheading>{title}</SettingsSubheading>
			<p className="mt-3 text-sm text-grey-600 max-w-[560px]">{description}</p>

			<div className="mt-4 rounded-lg border border-grey-200 p-4">
				<p className="text-sm font-medium text-grey-900">{abilitiesTitle}</p>
				<ul className="mt-3 space-y-2">
					{abilities.map((ability) => {
						const isOff = ability.setting !== null && settings !== undefined && !settings[ability.setting]
						const isUnknown = ability.setting !== null && settings === undefined

						return (
							<li
								key={ability.key}
								className={`flex items-start gap-2 text-sm ${
									isOff ? 'text-grey-400' : 'text-grey-600'
								}`}
							>
								{isOff
									? (
										<XIcon
											aria-hidden={true}
											className="mt-0.5 size-4 flex-shrink-0 text-red-500"
											strokeWidth={2.5}
										/>
									)
									: (
										<CheckIcon
											aria-hidden={true}
											className={`mt-0.5 size-4 flex-shrink-0 ${
												isUnknown ? 'text-grey-300' : 'text-green-500'
											}`}
											strokeWidth={2.5}
										/>
									)}
								<span>
									{ability.label}
									{isOff
										? (
											<span className="sr-only">
												{` (${t('settings.candidateCenter.candidateProfile.unavailable')})`}
											</span>
										)
										: null}
								</span>
							</li>
						)
					})}
				</ul>
			</div>
		</section>
	)
}

type SettingSwitchProps = {
	id: string
	label: string
	description: string
	checked: boolean
	isLoading: boolean
	disabled: boolean
	justSaved: boolean
	onToggle: () => void
}

function SettingSwitch(
	{ id, label, description, checked, isLoading, disabled, justSaved, onToggle }: SettingSwitchProps,
) {
	const { t } = useTranslation('common')

	return (
		<div className="flex items-start gap-4">
			{isLoading
				? <div className="mt-0.5 h-6 w-11 flex-shrink-0 rounded-full bg-grey-200 animate-pulse" />
				: (
					<Switch
						checked={checked}
						aria-labelledby={`${id}-label`}
						aria-describedby={`${id}-description`}
						onChange={onToggle}
						disabled={disabled}
						className="mt-0.5"
					/>
				)}
			<div>
				<div className="flex items-center gap-2">
					<p id={`${id}-label`} className="text-sm font-medium text-grey-900">{label}</p>
					<AnimatePresence>
						{justSaved
							? (
								<motion.span
									key="saved"
									initial={{ opacity: 0, scale: 0.85 }}
									animate={{ opacity: 1, scale: 1 }}
									exit={{ opacity: 0, scale: 0.85 }}
									transition={{ duration: 0.15 }}
									className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200"
								>
									<CheckIcon className="size-3" strokeWidth={2.5} />
									{t('settings.candidateCenter.candidateProfile.saved')}
								</motion.span>
							)
							: null}
					</AnimatePresence>
				</div>
				<p id={`${id}-description`} className="mt-0.5 text-sm text-grey-500">{description}</p>
			</div>
		</div>
	)
}
